import { FilterCriteriaItem, PageParam } from './appDomain';
import { MeasurementUnitEnum } from './measurementDomain';
import { pick, keys } from 'lodash-es';

export interface Material {
  /**
   * The unit the numbers in a write were typed in. Request-only: never stored on the row and
   * never sent back, but it has to live on this type so `buildUpdate/CreateMatParam` can carry
   * it through. Dropping it stored a metric 210 as 210in.
   */
  enteredUnit?: MeasurementUnitEnum;

  /** Metric twin, sent alongside so switching units needs no refetch. */
  widthMm?: number;
  lengthMm?: number;
  caliperUm?: number;
  weightKg?: number;
  mweightKg?: number;
  volumeCubicM?: number;
  outsideDiameterMm?: number;
  coreDiameterMm?: number;

  /**
   * Read-only, already in the viewer's unit (gsm when metric). The raw `basisWeight` stays
   * as the supplier typed it because the paper picker matches its options on that one.
   */
  basisWeightMetric?: string;

  active: boolean;
  isBase: boolean;
  displayInMyPaper: boolean;
  isPublic: boolean;
  baseRefId: number;
  baseUom: string;
  basisWeight: string;
  caliperIn: number;
  category: string;
  color: string;
  copyBaseId: number;
  coreDiameterIn: number;
  costUnitType: string;
  createAt: string;
  description: string;
  disableAt: string;
  filter1: string;
  filter2: string;
  constraint1: string;
  constraint2: string;
  finish: string;
  fscCert: string;
  greensealCert: string;
  heightIn: number;
  id: number;
  leadTimeHr: number;
  lengthIn: number;
  manufacturerName: string;
  manufacturerSku: string;
  markupPct: number | null;
  mweightLb: number;
  name: string;
  nup: number;
  orgId: number;
  outsideDiameterIn: number;
  packageQty: number;
  packageUnit: string;
  pcwRecycledPct: number;
  priceUom: string;
  priceUsd: number;
  salesTaxUsd: number;
  sfiCert: string;
  sheets: number;
  skuTypeFilter: number[];
  sqIn: number;
  stockType: string;
  svcTypeId: number;
  thicknessIn: number;
  totalPrice: number;
  unitCostUsd: number;
  updateAt: string;
  vendorId: number;
  vendorSku: string;
  version: number;
  volumeCubicFt: number;
  weightLb: number;
  widthIn: number;
}

export type PartialMat = Partial<Material>;

export interface ListMatParam {
  page: PageParam;
  query?: MatFilterObj;
}

export type UpdateMatParam = Omit<
  PartialMat,
  'isBase' | 'baseRefId' | 'createAt' | 'updateAt' | 'orgId' | 'deleted'
>;

export type CreateMatParam = Omit<
  PartialMat,
  'createAt' | 'updateAt' | 'orgId' | 'deleted' | 'version'
>;

export const EMPTY_MAT: PartialMat = {
  isPublic: true,
  active: true,
  displayInMyPaper: true,
  baseRefId: 0,
  baseUom: '',
  basisWeight: '',
  caliperIn: 0,
  category: '',
  color: '',
  copyBaseId: 0,
  coreDiameterIn: 0,
  costUnitType: '',
  description: '',
  filter1: '',
  filter2: '',
  finish: '',
  fscCert: '',
  greensealCert: '',
  heightIn: 0,
  isBase: true,
  leadTimeHr: 0,
  lengthIn: 0,
  markupPct: null,
  manufacturerName: '',
  manufacturerSku: '',
  mweightLb: 0,
  name: '',
  nup: 0,
  constraint1: '',
  constraint2: '',
  outsideDiameterIn: 0,
  packageQty: 0,
  packageUnit: '',
  pcwRecycledPct: 0,
  priceUom: '',
  priceUsd: 0,
  salesTaxUsd: 0,
  sfiCert: '',
  sheets: 0,
  skuTypeFilter: [],
  sqIn: 0,
  stockType: '',
  svcTypeId: 0,
  thicknessIn: 0,
  totalPrice: 0,
  unitCostUsd: 0,
  vendorId: 0,
  vendorSku: '',
  volumeCubicFt: 0,
  weightLb: 0,
  widthIn: 0,
};

/**
 * `enteredUnit` is picked explicitly rather than listed in {@link EMPTY_MAT}.
 *
 * That constant doubles as the blank record a create form starts from, and a unit does not
 * belong there - it describes a particular submission, not an empty material. Left out of the
 * picked keys entirely, though, it was stripped from every save: a width typed as 210 in a
 * metric form reached the API with no declaration and was stored as 210in.
 */
export function buildUpdateMatParam(mat: PartialMat): UpdateMatParam {
  return pick(mat, keys(EMPTY_MAT), 'enteredUnit', 'id', 'version');
}

export function buildCreateMatParam(mat: PartialMat): CreateMatParam {
  return pick(mat, keys(EMPTY_MAT), 'enteredUnit');
}

export interface MatFilterObj {
  id?: FilterCriteriaItem;
  name?: FilterCriteriaItem;
  active?: boolean;
  category?: FilterCriteriaItem;
  color?: FilterCriteriaItem;
  finish?: FilterCriteriaItem;
  basisWeight?: FilterCriteriaItem;
  widthIn?: FilterCriteriaItem;
  lengthIn?: FilterCriteriaItem;
  markupPct?: FilterCriteriaItem;
  /**
   * The unit the numeric criteria above were typed in.
   *
   * Travels with the filter rather than being read at the moment of the request: the list is
   * re-fetched on every page change and sort, long after the panel that typed `widthIn: 210` has
   * closed, and a 210 matched against inches finds nothing.
   */
  enteredUnit?: MeasurementUnitEnum;
}
